/* ==========================================
   ARTICLE FAQ ACCORDION + DYNAMIC FAQ SCHEMA
   Includes forced inline styles for theme overrides
========================================== */

document.addEventListener('DOMContentLoaded', function () {
  const article = document.querySelector('.article-faq-content');

  if (!article) return;

  const faqSchemaItems = [];

  /* ==========================================
     HELPERS
  ========================================== */

  function getText(element) {
    return (element.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getHeadingLevel(element) {
    const match = element.tagName.match(/^H([1-6])$/);
    return match ? Number(match[1]) : null;
  }

  function isFaqHeading(element) {
    if (!/^H[1-6]$/.test(element.tagName)) {
      return false;
    }

    const text = getText(element)
      .toLowerCase()
      .replace(/[?:\-–—]/g, '')
      .trim();

    return (
      text === 'faq' ||
      text === 'faqs' ||
      text === 'frequently asked questions' ||
      text === 'frequently asked question'
    );
  }

  function cleanQuestionText(text) {
    return text
      .replace(
        /^(?:q(?:uestion)?\s*[\.\:\-\)]\s*)/i,
        ''
      )
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanAnswerElement(element) {
    const clone = element.cloneNode(true);

    const walker = document.createTreeWalker(
      clone,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      if (!node.textContent.trim()) continue;

      node.textContent = node.textContent.replace(
        /^\s*(?:a(?:nswer)?\s*[\.\:\-\)]\s*)/i,
        ''
      );

      break;
    }

    return clone;
  }

  /* ==========================================
     NORMALIZE EDITOR WRAPPERS
  ========================================== */

  function isEditorWrapper(element) {
    if (element.tagName !== 'DIV') return false;

    if (
      element.classList.contains('article-faq-accordion') ||
      element.classList.contains('faq-item')
    ) {
      return false;
    }

    const children = Array.from(element.children);

    if (!children.length) return false;

    if (
      element.classList.contains('OutlineElement') ||
      element.getAttribute('data-mce-style') ||
      element.getAttribute('data-mce-fragment')
    ) {
      return true;
    }

    if (
      children.length === 1 &&
      /^(P|H1|H2|H3|H4|H5|H6)$/.test(
        children[0].tagName
      )
    ) {
      return true;
    }

    return false;
  }

  function unwrapElement(element) {
    const parent = element.parentNode;

    if (!parent) return;

    while (element.firstChild) {
      parent.insertBefore(
        element.firstChild,
        element
      );
    }

    element.remove();
  }

  Array.from(article.querySelectorAll('div'))
    .reverse()
    .forEach(function (div) {
      if (isEditorWrapper(div)) {
        unwrapElement(div);
      }
    });

  /* ==========================================
     QUESTION DETECTION
  ========================================== */

  function isQuestion(element, faqHeadingLevel) {
    const text = getText(element);

    if (!text) return false;

    const headingLevel =
      getHeadingLevel(element);

    if (
      headingLevel &&
      headingLevel > faqHeadingLevel
    ) {
      return true;
    }

    if (
      /^(?:q(?:uestion)?\s*[\.\:\-\)])/i.test(text)
    ) {
      return true;
    }

    if (element.tagName === 'P') {
      const bold = element.querySelector(
        ':scope > strong, :scope > b'
      );

      if (
        bold &&
        getText(bold).includes('?')
      ) {
        return true;
      }
    }

    if (
      element.tagName === 'P' &&
      text.includes('?')
    ) {
      const questionPart =
        text.split('?')[0];

      if (
        questionPart.length <= 250 &&
        questionPart.split(/\s+/).length <= 35
      ) {
        return true;
      }
    }

    if (
      element.tagName === 'DIV' &&
      element.children.length === 0 &&
      text.includes('?') &&
      text.indexOf('?') <= 250
    ) {
      return true;
    }

    return false;
  }

  /* ==========================================
     EXTRACT QUESTION + INLINE ANSWER
  ========================================== */

  function extractQuestionAndAnswer(element) {
    const fullText = getText(element);

    const bold = element.querySelector(
      ':scope > strong, :scope > b'
    );

    if (
      bold &&
      getText(bold).includes('?')
    ) {
      let firstMeaningfulNode =
        element.firstChild;

      while (
        firstMeaningfulNode &&
        firstMeaningfulNode.nodeType === Node.TEXT_NODE &&
        !firstMeaningfulNode.textContent.trim()
      ) {
        firstMeaningfulNode =
          firstMeaningfulNode.nextSibling;
      }

      if (firstMeaningfulNode === bold) {
        const question =
          cleanQuestionText(
            getText(bold)
          );

        const answerElement =
          element.cloneNode(true);

        const clonedBold =
          answerElement.querySelector(
            ':scope > strong, :scope > b'
          );

        if (clonedBold) {
          clonedBold.remove();
        }

        const answerText =
          getText(answerElement);

        if (answerText) {
          return {
            question,
            answers: [
              cleanAnswerElement(answerElement)
            ]
          };
        }

        return {
          question,
          answers: []
        };
      }
    }

    const questionMark =
      fullText.indexOf('?');

    if (questionMark !== -1) {
      const question =
        cleanQuestionText(
          fullText.slice(
            0,
            questionMark + 1
          )
        );

      const answerText =
        fullText
          .slice(questionMark + 1)
          .trim();

      if (!answerText) {
        return {
          question,
          answers: []
        };
      }

      const clone =
        element.cloneNode(true);

      const walker =
        document.createTreeWalker(
          clone,
          NodeFilter.SHOW_TEXT
        );

      let node;
      let foundQuestionMark = false;

      while ((node = walker.nextNode())) {
        if (foundQuestionMark) break;

        const markIndex =
          node.textContent.indexOf('?');

        if (markIndex !== -1) {
          node.textContent =
            node.textContent.slice(
              markIndex + 1
            );

          foundQuestionMark = true;
        } else {
          node.textContent = '';
        }
      }

      if (
        !foundQuestionMark ||
        !getText(clone)
      ) {
        const paragraph =
          document.createElement('p');

        paragraph.textContent =
          answerText;

        return {
          question,
          answers: [paragraph]
        };
      }

      return {
        question,
        answers: [
          cleanAnswerElement(clone)
        ]
      };
    }

    return {
      question:
        cleanQuestionText(fullText),
      answers: []
    };
  }

  /* ==========================================
     FIND FAQ SECTIONS
  ========================================== */

  const faqHeadings = Array.from(
    article.querySelectorAll(
      'h1,h2,h3,h4,h5,h6'
    )
  ).filter(isFaqHeading);

  if (!faqHeadings.length) return;

  faqHeadings.forEach(function (
    faqHeading,
    sectionIndex
  ) {
    if (
      faqHeading.dataset.faqProcessed ===
      'true'
    ) {
      return;
    }

    const faqHeadingLevel =
      getHeadingLevel(faqHeading) || 2;

    const faqGroups = [];

    let currentQuestion = null;
    let currentAnswers = [];
    let currentElements = [];

    function saveCurrentGroup() {
      if (!currentQuestion) return;

      if (currentAnswers.length) {
        faqGroups.push({
          question: currentQuestion,
          answers: currentAnswers,
          originalElements:
            currentElements
        });
      }

      currentQuestion = null;
      currentAnswers = [];
      currentElements = [];
    }

    let current =
      faqHeading.nextElementSibling;

    /* ========================================
       READ FAQ CONTENT
    ======================================== */

    while (current) {
      const next =
        current.nextElementSibling;

      const currentHeadingLevel =
        getHeadingLevel(current);

      if (
        currentHeadingLevel &&
        currentHeadingLevel <=
          faqHeadingLevel
      ) {
        break;
      }

      if (
        current.classList.contains(
          'article-faq-accordion'
        )
      ) {
        current = next;
        continue;
      }

      if (
        isQuestion(
          current,
          faqHeadingLevel
        )
      ) {
        saveCurrentGroup();

        const extracted =
          extractQuestionAndAnswer(
            current
          );

        currentQuestion =
          extracted.question;

        currentAnswers =
          extracted.answers;

        currentElements = [current];

      } else if (currentQuestion) {
        currentAnswers.push(
          cleanAnswerElement(current)
        );

        currentElements.push(current);
      }

      current = next;
    }

    saveCurrentGroup();

    if (!faqGroups.length) {
      return;
    }

    /* ==========================================
       BUILD ACCORDION
    ========================================== */

    const accordion =
      document.createElement('div');

    accordion.className =
      'article-faq-accordion';

    accordion.setAttribute(
      'data-faq-section',
      String(sectionIndex + 1)
    );

    /*
     * Force accordion itself to use full width.
     */
    accordion.style.setProperty(
      'display',
      'block',
      'important'
    );

    accordion.style.setProperty(
      'width',
      '100%',
      'important'
    );

    accordion.style.setProperty(
      'max-width',
      '100%',
      'important'
    );

    accordion.style.setProperty(
      'box-sizing',
      'border-box',
      'important'
    );

    faqGroups.forEach(function (
      group,
      index
    ) {
      const questionText =
        cleanQuestionText(
          group.question
        );

      if (!questionText) return;

      const answerText =
        group.answers
          .map(function (element) {
            return getText(element);
          })
          .filter(Boolean)
          .join(' ')
          .trim();

      if (!answerText) return;

      /* --------------------------
         FAQ ITEM
      -------------------------- */

      const item =
        document.createElement('div');

      item.className = 'faq-item';

      item.style.setProperty(
        'display',
        'block',
        'important'
      );

      item.style.setProperty(
        'width',
        '100%',
        'important'
      );

      item.style.setProperty(
        'max-width',
        '100%',
        'important'
      );

      item.style.setProperty(
        'box-sizing',
        'border-box',
        'important'
      );

      /* --------------------------
         QUESTION BUTTON
      -------------------------- */

      const questionButton =
        document.createElement('button');

      questionButton.type = 'button';

      questionButton.className =
        'faq-question';

      const answerId =
        `faq-answer-${sectionIndex}-${index}`;

      questionButton.setAttribute(
        'aria-expanded',
        'false'
      );

      questionButton.setAttribute(
        'aria-controls',
        answerId
      );

      /*
       * Force button to full width because
       * this theme is overriding our CSS.
       */
      questionButton.style.setProperty(
        'display',
        'flex',
        'important'
      );

      questionButton.style.setProperty(
        'width',
        '100%',
        'important'
      );

      questionButton.style.setProperty(
        'min-width',
        '100%',
        'important'
      );

      questionButton.style.setProperty(
        'max-width',
        '100%',
        'important'
      );

      questionButton.style.setProperty(
        'align-items',
        'center',
        'important'
      );

      questionButton.style.setProperty(
        'box-sizing',
        'border-box',
        'important'
      );

      questionButton.style.setProperty(
        'text-align',
        'left',
        'important'
      );

      questionButton.style.setProperty(
        'margin',
        '0',
        'important'
      );

      /* Question text */

      const questionTextElement =
        document.createElement('span');

      questionTextElement.className =
        'faq-question-text';

      questionTextElement.textContent =
        questionText;

      /*
       * Allow question text to take all
       * available space before the icon.
       */
      questionTextElement.style.setProperty(
        'display',
        'block',
        'important'
      );

      questionTextElement.style.setProperty(
        'flex',
        '1 1 auto',
        'important'
      );

      questionTextElement.style.setProperty(
        'min-width',
        '0',
        'important'
      );

      questionTextElement.style.setProperty(
        'text-align',
        'left',
        'important'
      );

      /* ======================================
         REAL PLUS / MINUS ICON
      ====================================== */

      const icon =
        document.createElement('span');

      icon.className = 'faq-icon';

      icon.textContent = '+';

      icon.setAttribute(
        'aria-hidden',
        'true'
      );

      /*
       * Force icon to far-right.
       */
      icon.style.setProperty(
        'display',
        'inline-flex',
        'important'
      );

      icon.style.setProperty(
        'align-items',
        'center',
        'important'
      );

      icon.style.setProperty(
        'justify-content',
        'center',
        'important'
      );

      icon.style.setProperty(
        'flex',
        '0 0 30px',
        'important'
      );

      icon.style.setProperty(
        'width',
        '30px',
        'important'
      );

      icon.style.setProperty(
        'min-width',
        '30px',
        'important'
      );

      icon.style.setProperty(
        'height',
        '30px',
        'important'
      );

      /*
       * THIS is the critical part.
       */
      icon.style.setProperty(
        'margin-left',
        'auto',
        'important'
      );

      icon.style.setProperty(
        'margin-right',
        '0',
        'important'
      );

      icon.style.setProperty(
        'font-family',
        'Arial, sans-serif',
        'important'
      );

      icon.style.setProperty(
        'font-size',
        '30px',
        'important'
      );

      icon.style.setProperty(
        'font-weight',
        '300',
        'important'
      );

      icon.style.setProperty(
        'line-height',
        '1',
        'important'
      );

      icon.style.setProperty(
        'opacity',
        '1',
        'important'
      );

      icon.style.setProperty(
        'visibility',
        'visible',
        'important'
      );

      icon.style.setProperty(
        'pointer-events',
        'none',
        'important'
      );

      questionButton.appendChild(
        questionTextElement
      );

      questionButton.appendChild(icon);

      /* --------------------------
         ANSWER
      -------------------------- */

      const answer =
        document.createElement('div');

      answer.className = 'faq-answer';

      answer.id = answerId;

      answer.hidden = true;

      group.answers.forEach(function (
        answerElement
      ) {
        answer.appendChild(
          answerElement.cloneNode(true)
        );
      });

      item.appendChild(
        questionButton
      );

      item.appendChild(answer);

      accordion.appendChild(item);

      /* ======================================
         ADD TO FAQ SCHEMA
      ====================================== */

      faqSchemaItems.push({
        '@type': 'Question',

        name: questionText,

        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText
        }
      });
    });

    if (!accordion.children.length) {
      return;
    }

    /*
     * Insert accordion after FAQ heading.
     */
    faqHeading.insertAdjacentElement(
      'afterend',
      accordion
    );

    /*
     * Remove original FAQ content.
     */
    faqGroups.forEach(function (group) {
      group.originalElements.forEach(
        function (element) {
          element.remove();
        }
      );
    });

    faqHeading.dataset.faqProcessed =
      'true';

    /* ==========================================
       OPEN / CLOSE
    ========================================== */

    accordion
      .querySelectorAll(
        '.faq-question'
      )
      .forEach(function (button) {
        button.addEventListener(
          'click',
          function () {
            const item =
              button.closest(
                '.faq-item'
              );

            if (!item) return;

            const answer =
              item.querySelector(
                '.faq-answer'
              );

            if (!answer) return;

            const icon =
              button.querySelector(
                '.faq-icon'
              );

            const isOpen =
              button.getAttribute(
                'aria-expanded'
              ) === 'true';

            button.setAttribute(
              'aria-expanded',
              String(!isOpen)
            );

            answer.hidden =
              isOpen;

            item.classList.toggle(
              'is-open',
              !isOpen
            );

            if (icon) {
              icon.textContent =
                isOpen ? '+' : '−';
            }
          }
        );
      });
  });

  /* ==========================================
     DYNAMIC FAQPAGE JSON-LD
  ========================================== */

  if (
    faqSchemaItems.length > 0 &&
    !document.getElementById(
      'article-faq-jsonld'
    )
  ) {
    const faqSchema = {
      '@context':
        'https://schema.org',

      '@type':
        'FAQPage',

      mainEntity:
        faqSchemaItems
    };

    const schemaScript =
      document.createElement('script');

    schemaScript.type =
      'application/ld+json';

    schemaScript.id =
      'article-faq-jsonld';

    schemaScript.textContent =
      JSON.stringify(faqSchema)
        .replace(
          /</g,
          '\\u003c'
        );

    document.head.appendChild(
      schemaScript
    );
  }
});